import {Price as BookingPrice} from '@daedalus/core/src/api-types/bovio/response/booking'
import {RoomPrice} from '@findhotel/sapi'

import {BookingOfferPrice} from '../../booking/types/BookingData'
import {OfferPrice} from '../../offer/types/offer'
import PriceTypes from '../../offer/types/PriceTypes'
import {toFloatWithTwoDecimals} from '../../utils/number'

export type Price = OfferPrice | BookingOfferPrice | BookingPrice

export enum ShortCurrencySymbols {
  EUR = '€',
  GBP = '£',
  ILS = '₪',
  INR = '₹',
  JPY = '¥',
  KRW = '₩',
  PHP = '₱',
  USD = '$'
}

export const HOTEL_FEES_RESORT_FEE = 'resort_fee'
export const HOTEL_FEES_TAX = 'tax'
export const HOTEL_FEES_OTHER = 'other'

type HotelFee =
  | typeof HOTEL_FEES_RESORT_FEE
  | typeof HOTEL_FEES_TAX
  | typeof HOTEL_FEES_OTHER

export const getPriceByType = <T extends Price>(
  type: string,
  prices: T[]
): T | undefined => {
  return prices.find(price => price.type === type)
}

/**
 * Check whether is there a Currency Conversion
 * A currency conversion is detected when a set of prices contains a DISPLAY_CURRENCY price type
 * @param prices - The list of offer/booking prices
 */
export const isThereACurrencyConversion = (prices: Price[]) => {
  return Boolean(getPriceByType(PriceTypes.DISPLAY_PRICE, prices))
}

/**
Returns the price the user sees. Not necessarily the price in which
he/she was or will be charged
* @param prices - The list of offer/booking prices
 */
export const getDisplayPrice = <T extends Price>(
  prices: T[]
): T | undefined => {
  const type = isThereACurrencyConversion(prices)
    ? PriceTypes.DISPLAY_PRICE
    : PriceTypes.CHARGEABLE_PRICE
  return getPriceByType(type, prices)
}

/**
  Gets the price with the type PriceTypes.CHARGEABLE_PRICE
  * @param prices - The list of offer/booking prices
 */
export const getChargeableCurrencyPrice = (prices: Price[]) =>
  getPriceByType(PriceTypes.CHARGEABLE_PRICE, prices)

/**
 Returns the total price including hotel fees regardless of tax display logic
 * @param price - The price object
 */
export const getTotalPrice = (price?: Price | RoomPrice) => {
  const total = Number(price?.chargeable.base) + Number(price?.chargeable.taxes)
  const hotelFees = Number(price?.hotelFees?.total) || 0
  return toFloatWithTwoDecimals(total + hotelFees) || 0
}

/**
 * Returns the nightly price from any total
 */
export const getNightlyPrice = (
  price: number | null | undefined | string,
  numberOfNights: number,
  numberOfRooms = 1
) => {
  if (Number.isNaN(price)) return null

  return toFloatWithTwoDecimals(Number(price) / numberOfNights / numberOfRooms)
}

/**
 * Returns the discount applied to the chargeable amount
 * @param price - The price object
 */
export const getPriceDiscount = (price?: Price) => {
  return price?.chargeable.discount || 0
}

/**
 * to convert price types.
 * This converts the price types we receive in the sapi.rooms() and /bookings request to the ones we receive in /offer-check
 * The objective of this function is to provide backwards compatibility to the old price types
 * @internal
 * @param price - The list of offer prices
 */
export const convertPricesToNewFormat = <T extends Price>(price: T): T => {
  const conversionList = [
    {from: PriceTypes.CHARGEABLE_CURRENCY, to: PriceTypes.CHARGEABLE_PRICE},
    {from: PriceTypes.USER_CURRENCY, to: PriceTypes.DISPLAY_PRICE}
  ]

  const copyPrice = {...price}
  for (const mappingItem of conversionList) {
    if (copyPrice.type === mappingItem.from) {
      // eslint-disable-next-line fp/no-mutation
      copyPrice.type = mappingItem.to
    }
  }
  return copyPrice
}

/**
 Returns the total price excluding hotel fees regardless of tax display logic, unless it is a pay at property deal, then it returns 0
 */
export const getTotalUpFrontPrice = (
  price?: Price,
  isPayAtProperty?: boolean
) => {
  if (isPayAtProperty || !price) return 0
  return toFloatWithTwoDecimals(
    Number(price.chargeable.base) + Number(price.chargeable.taxes)
  )
}

/**
 Returns the total hotel fees, unless it is a pay at property deal, then it returns the total price
 * @param price - an offer price object
 * @param isPayAtProperty - a boolean that represents if the offer is pay at property
 */
export const getTotalAtProperty = (
  price?: Price,
  isPayAtProperty?: boolean
) => {
  if (isPayAtProperty) return getTotalPrice(price)
  return toFloatWithTwoDecimals(price?.hotelFees?.total || 0)
}

/**
 * Returns the total price to be paid upfront in cents
 * @param price - an offer price object
 * @param canPayLater - a boolean that represents if the offer is pay at property
 */
export const getTotalUpFrontPriceAsCents = (
  price: Price,
  canPayLater: boolean
) => {
  return Math.round(Number(getTotalUpFrontPrice(price, canPayLater)) * 100)
}

const getHotelFeeByType = (type: HotelFee, price?: Price) =>
  Number(
    price?.hotelFees?.breakdown?.find(fee => fee.type === type)?.total || 0
  )

export const getHotelFeesResortFee = (price?: Price) =>
  getHotelFeeByType(HOTEL_FEES_RESORT_FEE, price)
export const getHotelFeesTax = (price?: Price) =>
  getHotelFeeByType(HOTEL_FEES_TAX, price)
export const getHotelFeesOther = (price?: Price) =>
  getHotelFeeByType(HOTEL_FEES_OTHER, price)

/**
 * Returns the different pricing and currency information for an offer
 * @param prices - List of prices for an offer
 * @param isPayAtProperty - a boolean that represents if the offer is pay at property
 */
export const getPriceDetails = (prices: Price[], isPayAtProperty: boolean) => {
  const displayPrice = getDisplayPrice(prices)

  if (!displayPrice)
    return {
      totalDisplayPrice: 0,
      totalUpFrontDisplayPrice: 0,
      totalAtPropertyDisplayPrice: 0,
      totalUpFrontChargeablePrice: 0,
      totalAtPropertyChargeablePrice: 0,
      displayCurrencyCode: undefined,
      chargeableCurrencyCode: undefined,
      taxesDisplayPrice: 0,
      hotelFeesResortFee: 0,
      hotelFeesTax: 0,
      hotelFeesOther: 0,
      hotelFeesTotal: 0
    }

  const {currencyCode: displayCurrencyCode, taxes: taxesDisplayPrice} =
    displayPrice.chargeable

  const totalDisplayPrice = getTotalPrice(displayPrice)
  const hotelFeesResortFee = getHotelFeesResortFee(displayPrice) ?? 0
  const hotelFeesTax = getHotelFeesTax(displayPrice) ?? 0
  const hotelFeesOther = getHotelFeesOther(displayPrice) ?? 0

  const chargeablePrice = getChargeableCurrencyPrice(prices)

  const chargeableCurrencyCode = chargeablePrice?.chargeable.currencyCode
  const payAtHotel = chargeablePrice?.payAtHotel

  const totalAtPropertyDisplayPrice = getTotalAtProperty(
    displayPrice,
    isPayAtProperty
  )
  const totalUpFrontDisplayPrice = getTotalUpFrontPrice(
    displayPrice,
    isPayAtProperty
  )

  const totalUpFrontChargeablePrice = getTotalUpFrontPrice(
    chargeablePrice,
    isPayAtProperty
  )

  const totalAtPropertyChargeablePrice = payAtHotel
    ? payAtHotel.total
    : getTotalAtProperty(chargeablePrice, isPayAtProperty)

  return {
    totalDisplayPrice: totalDisplayPrice ?? 0,
    totalUpFrontDisplayPrice: totalUpFrontDisplayPrice ?? 0,
    totalAtPropertyDisplayPrice: totalAtPropertyDisplayPrice ?? 0,
    totalUpFrontChargeablePrice: totalUpFrontChargeablePrice ?? 0,
    totalAtPropertyChargeablePrice: totalAtPropertyChargeablePrice ?? 0,
    displayCurrencyCode,
    chargeableCurrencyCode,
    taxesDisplayPrice: taxesDisplayPrice ?? 0,
    hotelFeesResortFee,
    hotelFeesTax,
    hotelFeesOther,
    hotelFeesTotal: hotelFeesResortFee + hotelFeesTax + hotelFeesOther
  }
}
